import React, { useRef, useMemo } from "react"
import { observer } from "mobx-react"

import {
    BreadcrumbTooltipButton,
    containerStyle,
} from "src/components/SegmentPicker/components/ListItemBreadcrumb/styled"
import { Tooltip } from "src/components/Tooltips/Tooltip"
import { IListItemBreadcrumbProps } from "src/components/SegmentPicker/components/ListItemBreadcrumb/types"
import { toPathPairs } from "src/components/SegmentPicker/components/ListItemBreadcrumb/helpers/toPathPairs"
import { useTruncation } from "src/components/SegmentPicker/components/ListItemBreadcrumb/helpers/useTruncation"
import { BreadcrumbBuilder } from "src/components/SegmentPicker/components/ListItemBreadcrumb/sub-components/BreadcrumbBuilder"
import { BreadcrumbDisplay } from "src/components/SegmentPicker/components/ListItemBreadcrumb/sub-components/BreadcrumbDisplay"

export const ListItemBreadcrumb = observer(
    (props: IListItemBreadcrumbProps) => {
        const containerRef = useRef<HTMLSpanElement>(null)

        // Process path data - memoized for stability
        const pathData = useMemo(() => {
            const parts = props.segment.path.split(" / ")
            const pairs = toPathPairs(parts)

            return {
                allPathPairs: pairs,
                hasMultipleParts: parts.length > 2,
                fullText: parts.join(" / "),
            }
        }, [props.segment.path])

        // Extract for cleaner code
        const { allPathPairs, hasMultipleParts, fullText } = pathData

        // Use custom hook for truncation logic
        const shouldTruncate = useTruncation(
            containerRef,
            fullText,
            hasMultipleParts,
        )

        // Build tooltip content once
        const tooltipContent = useMemo(
            () => (
                <span>
                    {allPathPairs.map((pair, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && (
                                <span style={{ margin: "0 4px" }}>/</span>
                            )}
                            <BreadcrumbBuilder
                                pathPairs={[pair]}
                                segmentId={props.segment.id}
                                Component={BreadcrumbTooltipButton}
                            />
                        </React.Fragment>
                    ))}
                </span>
            ),
            [allPathPairs, props.segment.id],
        )

        // For short paths, render simple breadcrumb (no tooltip)
        if (!hasMultipleParts) {
            return (
                <span
                    style={containerStyle}
                    data-testid={`ListItemBreadcrumb/DefaultBreadcrumbWrapper-${props.segment.type}`}
                >
                    <BreadcrumbDisplay
                        allPathPairs={allPathPairs}
                        shouldTruncate={false}
                        segmentId={props.segment.id}
                    />
                </span>
            )
        }

        // For longer paths, render with tooltip and optional truncation
        return (
            <Tooltip
                disableInteractive={false}
                enterDelay={250}
                title={tooltipContent}
                arrow
            >
                <span
                    ref={containerRef}
                    style={containerStyle}
                    data-testid={`ListItemBreadcrumb/DefaultBreadcrumbWrapper-${props.segment.type}`}
                >
                    <BreadcrumbDisplay
                        allPathPairs={allPathPairs}
                        shouldTruncate={shouldTruncate}
                        segmentId={props.segment.id}
                    />
                </span>
            </Tooltip>
        )
    },
)
